import { BaseQueryFn, createApi } from '@reduxjs/toolkit/query/react';
import axios, { AxiosRequestConfig } from 'axios';
import { getToken } from '../../auth0';
import { config } from '../../config';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';
import { tenantSelector } from '../../selector/authenticationSelector';
import type { RootState } from '../../store/store';
import {
    AccountCancelResponse,
    AccountCancellationResponse,
    AccountDeletionRequestBody,
    AccountDeletionRequestResponse,
    AccountPermissionsResponse,
    AccountResponse,
    AccountUser,
    CreateTenantUserRequest,
    EnterTenantContractRequest,
    GetAccountResponse,
    HolidayStatus,
    HolidaysInRange,
    MasterTenantRow,
    OnboardingImportArgs,
    OnboardingImportFormat,
    OnboardingImportReport,
    OwnTermsContentRequest,
    RegisterTenantRequest,
    RegisterTenantResponse,
    ResendVerificationResponse,
    TenantAccountResponse,
    TenantDirectoryEntry,
    TenantFaqText,
    TenantInvoiceInfo,
    TenantPaymentIntegration,
    TenantPrivacyOfficer,
    UpdateAnredeRequest,
    UpdateBankDetailsRequest,
    UpdateBookingPageRequest,
    UpdateBrandingRequest,
    UpdateCheckInCheckOutConfigRequest,
    UpdateContactRequest,
    UpdateDesiredPaymentProviderRequest,
    UpdateHolidayRegionRequest,
    UpdateInvoiceIssuerRequest,
    UpdateLegalNoticeRequest,
    UpdateLegalTextsRequest,
    UpdateMoreToComeRequest,
    UpdateOrganizationRequest,
    UpdatePaymentsConfigRequest,
    UpdatePriceEntryRequest,
    UpdateProfileRequest,
    UpdateSeriesSeasonRequest,
    UpdateServicesRequest,
    UpdateTenantUserRequest,
} from './accountApiTypes';

// Inline base query — intentionally avoids importing from `../../utils/axiosBaseQuery`
// (which transitively imports `api/index.ts`, which imports `store/store.ts`).
// Going through that path would re-create a circular import the moment the
// store registers this slice (store → accountApiSlice → axiosBaseQuery →
// api/index → store), and webpack/HMR throws
// `Cannot access '__WEBPACK_DEFAULT_EXPORT__' before initialization` on boot.
//
// These endpoints are mounted at /admin WITHOUT tenantPrismaMiddleware, so the
// header never selected a database here — which is why it used to be omitted.
// Since #2521 it does something else: a login can hold a membership per tenant,
// and the backend uses `X-Tenant` to pick WHICH of the caller's own memberships
// a request is about (`lib/callerMembership.ts`). Omitting it would pin a
// multi-tenant user — in practice a kolula master — to their oldest membership,
// so the Account section would keep showing tenant A while the switcher says B.
//
// It is read through `tenantSelector` rather than the lagging
// `authentication.tenant` mirror, the same source that sets the header on every
// other request, so the two cannot disagree. `api.getState()` is used instead of
// importing the store, which is the circular import this inline base query
// exists to avoid in the first place.
const accountBaseQuery: BaseQueryFn<AxiosRequestConfig, unknown> = async (
    args,
    api,
) => {
    try {
        const token = await getToken();
        const tenant = tenantSelector(api.getState() as RootState) || '';
        const response = await axios({
            baseURL: config.api.newBackendUrl,
            headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
            signal: api.signal,
            ...args,
        });
        return { data: response.data };
    } catch (err: unknown) {
        if (axios.isAxiosError(err)) {
            return {
                error: {
                    status: err.response?.status,
                    data: err.response?.data || err.message,
                },
            };
        }
        return { error: err };
    }
};

/** The own-terms routes: the own account, or (master) the tenant in view by slug. */
const ownTermsBase = (slug: string | undefined): string =>
    (slug ? `/tenants/${encodeURIComponent(slug)}/own-terms` : '/account/own-terms');

/** Owner: the own account; master: the tenant in view, by slug. */
const onboardingImportUrl = (slug: string | undefined, preview: boolean): string => {
    const base = slug ? `/tenants/${encodeURIComponent(slug)}` : '/account';
    return `${base}/onboarding-import${preview ? '/preview' : ''}`;
};

/**
 * The multipart body: the file text and what is attached — counts only. The
 * server reads nothing else (2 MB, no file parts); photos and the terms PDF
 * are uploaded after the apply through their own routes.
 */
const onboardingImportForm = (args: OnboardingImportArgs): FormData => {
    const form = new FormData();
    form.append('file', args.fileText);
    const photos = Object.fromEntries(Object.entries(args.photos).map(([room, files]) => [room, files.length]));
    form.append('attachments', JSON.stringify({ photos, termsOfUse: !!args.termsOfUse }));
    return form;
};

/** GET/PUT/DELETE /admin/account/sender. */
export interface SenderIdentityResponse {
    sender: {
        address: string;
        domain: string;
        status: 'pending' | 'verified' | 'failed';
        requestedAt: string;
        verifiedAt: string | null;
        dnsRecords: { type: 'CNAME', name: string, value: string }[];
    } | null;
    /** Why the live SES check could not run — never shown as "pending". */
    checkError: string | null;
}

/**
 * A terms-document request body: multipart when it carries a PDF, JSON
 * otherwise. The backend route takes either (multer passes JSON through).
 */
const ownTermsBody = (
    request: { title?: string, file?: File, url?: string },
): FormData | { title?: string, url?: string } => {
    if (request.file) {
        const formData = new FormData();
        formData.append('document', request.file);
        if (request.title !== undefined) formData.append('title', request.title);
        return formData;
    }
    return {
        ...(request.title !== undefined ? { title: request.title } : {}),
        ...(request.url !== undefined ? { url: request.url } : {}),
    };
};

export const accountApiSlice = createApi({
    reducerPath: 'accountApi',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: accountBaseQuery,
    tagTypes: [
        'Account', 'AccountCancellation', 'TenantDirectory', 'MasterTenants', 'TenantAccount', 'AccountUsers',
        'PaymentIntegration', 'SenderIdentity', 'HolidayStatus',
    ],
    endpoints: (builder) => ({
        // POST /admin/tenants/register — idempotent: returns the existing
        // tenant + user if the caller's auth0_sub already maps to one.
        registerTenant: builder.mutation<RegisterTenantResponse, RegisterTenantRequest>({
            query: (body) => ({
                url: '/tenants/register',
                method: 'POST',
                data: body,
            }),
            invalidatesTags: ['Account'],
        }),
        // GET /admin/account — resolves { tenant, user } or
        // { onboardingPending: true } from the caller's auth0_sub.
        getAccount: builder.query<GetAccountResponse, void>({
            query: () => ({
                url: '/account',
                method: 'GET',
            }),
            providesTags: ['Account'],
        }),
        // GET /admin/account/permissions — DB-driven admin-UI permission set for
        // the authenticated caller (#1377). Resolved by auth0_sub (no X-Tenant
        // header), so it shares the register/account auth-only group. Returns
        // { role, permissions[] } — the source of truth the UI gates on,
        // replacing the former hardcoded SELF_SERVICE_OWNER_PERMISSIONS.
        getAccountPermissions: builder.query<AccountPermissionsResponse, void>({
            query: () => ({
                url: '/account/permissions',
                method: 'GET',
            }),
            providesTags: ['Account'],
        }),
        // GET /admin/account/users — the app_user rows on the caller's tenant
        // (owner first). Powers the Account "Users" section; readable by any
        // tenant user (the section renders read-only for non-owners).
        getAccountUsers: builder.query<AccountUser[], void>({
            query: () => ({
                url: '/account/users',
                method: 'GET',
            }),
            providesTags: ['AccountUsers'],
        }),
        // POST /admin/account/users — owner adds a colleague (with a role). The
        // backend is Auth0-backed (create the Auth0 user, store its sub + the
        // app_user row) and is NOT implemented yet: this ships the frontend
        // against the documented contract; the endpoint follows in a separate PR
        // (#802/#803 pattern). Invalidates the list so it refreshes on success.
        createTenantUser: builder.mutation<AccountUser, CreateTenantUserRequest>({
            query: (body) => ({
                url: '/account/users',
                method: 'POST',
                data: body,
            }),
            invalidatesTags: ['AccountUsers'],
        }),
        // PATCH /admin/account/users/:id — owner edits a colleague's details /
        // role (email is the Auth0 identity, not editable). Auth0-backed backend
        // is the same follow-up (#1593); ships against the documented contract.
        updateTenantUser: builder.mutation<AccountUser, { id: string, body: UpdateTenantUserRequest }>({
            query: ({ id, body }) => ({
                url: `/account/users/${id}`,
                method: 'PATCH',
                data: body,
            }),
            invalidatesTags: ['AccountUsers'],
        }),
        // DELETE /admin/account/users/:id — owner removes a colleague. Like the
        // create endpoint, the Auth0-backed backend is not implemented yet (see
        // #1593); the frontend ships against the documented contract. Guards
        // (can't remove yourself / the last user) are enforced in the UI and
        // will be re-enforced server-side.
        deleteTenantUser: builder.mutation<void, string>({
            query: (id) => ({
                url: `/account/users/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['AccountUsers'],
        }),
        // GET /admin/tenants — directory of onboarded operational-DB tenants
        // for the master switcher. Backend gates on the `master` role (403
        // otherwise) and returns slug-bearing tenants that are active or
        // archived (#1379, marked by `status`).
        getAllTenants: builder.query<TenantDirectoryEntry[], void>({
            query: () => ({
                url: '/tenants',
                method: 'GET',
            }),
            providesTags: ['TenantDirectory'],
        }),
        // GET /admin/tenants/:slug/invoice-info — master-only. Company/legal/bank
        // details for a single tenant by slug, so a master viewing that tenant
        // can render an invoice (getAccount resolves to the master's own tenant,
        // not the viewed one). Backend gates on the `master` role (403 otherwise).
        getTenantInvoiceInfo: builder.query<TenantInvoiceInfo, string>({
            query: (slug) => ({
                url: `/tenants/${slug}/invoice-info`,
                method: 'GET',
            }),
        }),
        // GET /admin/tenants/:slug/account — master-only. The viewed tenant's
        // full account for a read-only master view (getAccount resolves to the
        // master's own tenant, not the viewed one). Backend gates on `master`.
        getTenantAccount: builder.query<TenantAccountResponse, string>({
            query: (slug) => ({
                url: `/tenants/${slug}/account`,
                method: 'GET',
            }),
            providesTags: (_result, _error, slug) => [{ type: 'TenantAccount', id: slug }],
        }),
        // PATCH /admin/account/profile — updates first/last name + phone
        // on the caller's app_user row. Returns the refreshed tenant+user
        // so getAccount cache stays in sync.
        updateProfile: builder.mutation<AccountResponse, UpdateProfileRequest>({
            query: (body) => ({
                url: '/account/profile',
                method: 'PATCH',
                data: body,
            }),
            invalidatesTags: ['Account'],
        }),
        // PATCH /admin/account/organization — tenant.organization_name + country.
        updateOrganization: builder.mutation<AccountResponse, UpdateOrganizationRequest>({
            query: (body) => ({
                url: '/account/organization',
                method: 'PATCH',
                data: body,
            }),
            invalidatesTags: ['Account'],
        }),
        // PATCH /admin/account/bank-details — writes tenant.config.bank.
        updateBankDetails: builder.mutation<AccountResponse, UpdateBankDetailsRequest>({
            query: (body) => ({
                url: '/account/bank-details',
                method: 'PATCH',
                data: body,
            }),
            invalidatesTags: ['Account'],
        }),
        // PATCH /admin/account/contact — writes tenant.config.contact. Separate
        // from bank-details so the contact section saves independently.
        updateContact: builder.mutation<AccountResponse, UpdateContactRequest>({
            query: (body) => ({
                url: '/account/contact',
                method: 'PATCH',
                data: body,
            }),
            invalidatesTags: ['Account'],
        }),
        // PATCH /admin/account/legal-notice — writes tenant.config.legalNotice.
        updateLegalNotice: builder.mutation<AccountResponse, UpdateLegalNoticeRequest>({
            query: (body) => ({
                url: '/account/legal-notice',
                method: 'PATCH',
                data: body,
            }),
            invalidatesTags: ['Account'],
        }),
        // PATCH /admin/account/booking-page — toggles tenant.config.endUserAppActive,
        // taking the shared end-user booking page (app.kolula.com/{slug}) live/dark.
        updateBookingPageActivation: builder.mutation<AccountResponse, UpdateBookingPageRequest>({
            query: (body) => ({
                url: '/account/booking-page',
                method: 'PATCH',
                data: body,
            }),
            invalidatesTags: ['Account'],
        }),
        // The tenant's own customer-mail sender (docs/plans/tenant-sender-domain.md).
        // GET re-checks SES live while the domain is pending, so it is refetched
        // on mount rather than served from cache.
        getSenderIdentity: builder.query<SenderIdentityResponse, void>({
            query: () => ({ url: '/account/sender', method: 'GET' }),
            providesTags: ['SenderIdentity'],
        }),
        setSenderIdentity: builder.mutation<SenderIdentityResponse, { address: string }>({
            query: (body) => ({ url: '/account/sender', method: 'PUT', data: body }),
            invalidatesTags: ['SenderIdentity'],
        }),
        removeSenderIdentity: builder.mutation<SenderIdentityResponse, void>({
            query: () => ({ url: '/account/sender', method: 'DELETE' }),
            invalidatesTags: ['SenderIdentity'],
        }),
        // PATCH /admin/account/anrede — writes tenant.config.anrede (du / Sie),
        // the German salutation for customer emails (#1642). Owner-gated backend.
        updateAnrede: builder.mutation<AccountResponse, UpdateAnredeRequest>({
            query: (body) => ({
                url: '/account/anrede',
                method: 'PATCH',
                data: body,
            }),
            invalidatesTags: ['Account'],
        }),
        // PATCH /admin/account/branding — multipart. Writes tenant.config.branding
        // (primary color + logo/favicon assets). Only the provided fields are
        // appended, so a save touches just what changed.
        updateBranding: builder.mutation<AccountResponse, UpdateBrandingRequest>({
            query: ({ primaryColor, logo, favicon }) => {
                const formData = new FormData();
                if (primaryColor !== undefined) formData.append('primaryColor', primaryColor);
                if (logo) formData.append('logo', logo);
                if (favicon) formData.append('favicon', favicon);
                // Intentionally NO Content-Type header. Passing a FormData lets
                // axios set `multipart/form-data` WITH the boundary the server
                // needs to parse the body. It also matters for this slice's
                // inline base query (`headers: { Authorization }, ...args`): a
                // `headers` object here would REPLACE that object and drop the
                // Authorization token, 401-ing the request.
                return {
                    url: '/account/branding',
                    method: 'PATCH',
                    data: formData,
                };
            },
            invalidatesTags: ['Account'],
        }),
        // PATCH /admin/account/check-in-checkout — writes
        // tenant.config.checkInCheckOutFlow (generic check-in/check-out flow
        // config). Backend must enforce the entitlement server-side (403 if not
        // entitled) — never trust the client toggle.
        // PATCH /admin/account/payments — writes tenant.config.payments
        // (#1338). Replaces the whole block; the backend validates it, since
        // the database enforces nothing inside JSON.
        // GET /admin/account/payment-integration — whether this tenant can take
        // card payments. Derived from the SSM parameter the booking flow reads,
        // so it cannot claim a connection the booking flow does not have. A
        // slug (master viewing another tenant) hits the by-slug variant.
        getPaymentIntegration: builder.query<TenantPaymentIntegration, string | undefined>({
            query: (slug) => ({
                url: slug ? `/tenants/${slug}/payment-integration` : '/account/payment-integration',
                method: 'GET',
            }),
            providesTags: (_result, _error, slug) => [
                { type: 'PaymentIntegration', id: slug ?? 'own' },
            ],
        }),
        // Which use cases the tenant offers — the enable switch in Account
        // (service-configuration plan, phase 2). Enabling one makes its
        // configuration section appear in Services and its card appear in the
        // end-user app.
        updateServices: builder.mutation<AccountResponse, UpdateServicesRequest>({
            query: (body) => ({
                url: '/account/services',
                method: 'PATCH',
                data: body,
            }),
            invalidatesTags: ['Account'],
        }),
        // ADR-0012 WP0 — who issues the statutory invoice. One mutation, two
        // routes: with a slug the master by-slug endpoint (recording an
        // existing customer's arrangement, e.g. InnoDom: external), without it
        // the caller's own tenant. Both modes accepted since 2026-08-19
        // (kolula = preliminary stage).
        // Tenant overrides for the postpay legal texts (#1703). Same two-route
        // pattern as the invoice issuer beside it.
        updateLegalTexts: builder.mutation<AccountResponse, UpdateLegalTextsRequest>({
            // Own tenant only, matching the legal notice beside it: these are
            // the tenant's OWN contract terms, and no by-slug write exists for
            // that section either.
            query: (texts) => ({
                url: '/account/legal-texts',
                method: 'PATCH',
                data: texts,
            }),
            invalidatesTags: ['Account', 'TenantAccount'],
        }),
        // The tenant's own terms documents (ADR-0003 amendment 2026-10-02). Owner
        // or master (PO, 2026-10-30); with a slug, the master by-slug route
        // writes the VIEWED tenant. Resolved: {newBackendUrl}=/admin +
        // /account/own-terms/… or /tenants/{slug}/own-terms/… (accountRoutes.ts).
        addOwnTermsDocument: builder.mutation<AccountResponse, { title: string, slug?: string } & OwnTermsContentRequest>({
            query: ({ slug, ...request }) => ({
                url: `${ownTermsBase(slug)}/documents`,
                method: 'POST',
                data: ownTermsBody(request),
            }),
            invalidatesTags: ['Account', 'TenantAccount'],
        }),
        // A new title, new content (PDF or link), or both.
        updateOwnTermsDocument: builder.mutation<
            AccountResponse,
            { id: string, title?: string, file?: File, url?: string, slug?: string }
        >({
            query: ({ id, slug, ...request }) => ({
                url: `${ownTermsBase(slug)}/documents/${encodeURIComponent(id)}`,
                method: 'PUT',
                data: ownTermsBody(request),
            }),
            invalidatesTags: ['Account', 'TenantAccount'],
        }),
        removeOwnTermsDocument: builder.mutation<AccountResponse, { id: string, slug?: string }>({
            query: ({ id, slug }) => ({
                url: `${ownTermsBase(slug)}/documents/${encodeURIComponent(id)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Account', 'TenantAccount'],
        }),
        // The data protection officer on the privacy policy; null resets to the
        // tenant itself. Resolved: /admin + /account/privacy-officer.
        updatePrivacyOfficer: builder.mutation<AccountResponse, Partial<Record<keyof TenantPrivacyOfficer, string>> | null>({
            query: (officer) => ({ url: '/account/privacy-officer', method: 'PUT', data: officer }),
            invalidatesTags: ['Account', 'TenantAccount'],
        }),
        // The booking page's own browser-tab title and description; blanks fall
        // back to the tenant's name. Resolved: /admin + /account/seo.
        updateSeo: builder.mutation<AccountResponse, { title: string, description: string }>({
            query: (seo) => ({ url: '/account/seo', method: 'PUT', data: seo }),
            invalidatesTags: ['Account', 'TenantAccount'],
        }),
        reorderOwnTermsDocuments: builder.mutation<AccountResponse, { ids: string[], slug?: string }>({
            query: ({ ids, slug }) => ({ url: `${ownTermsBase(slug)}/order`, method: 'PUT', data: { ids } }),
            invalidatesTags: ['Account', 'TenantAccount'],
        }),
        // The tenant's own FAQ questions (ADR-0003 amendment 2026-10-06). Own
        // tenant only, like the terms above. `category` absent = tenant-wide.
        // Resolved: {newBackendUrl}=/admin + /account/faqs… → /admin/account/faqs…
        // (accountRoutes.ts).
        addFaq: builder.mutation<
            AccountResponse,
            { category?: string, question: TenantFaqText, answer: TenantFaqText }
        >({
            query: (body) => ({ url: '/account/faqs', method: 'POST', data: body }),
            invalidatesTags: ['Account', 'TenantAccount'],
        }),
        updateFaq: builder.mutation<
            AccountResponse,
            { id: string, question: TenantFaqText, answer: TenantFaqText }
        >({
            query: ({ id, ...body }) => ({
                url: `/account/faqs/${encodeURIComponent(id)}`,
                method: 'PUT',
                data: body,
            }),
            invalidatesTags: ['Account', 'TenantAccount'],
        }),
        removeFaq: builder.mutation<AccountResponse, string>({
            query: (id) => ({ url: `/account/faqs/${encodeURIComponent(id)}`, method: 'DELETE' }),
            invalidatesTags: ['Account', 'TenantAccount'],
        }),
        reorderFaqs: builder.mutation<AccountResponse, { category?: string, ids: string[] }>({
            query: (body) => ({ url: '/account/faqs/order', method: 'PUT', data: body }),
            invalidatesTags: ['Account', 'TenantAccount'],
        }),
        // The onboarding import (docs/plans/onboarding-import.md): the format the
        // upload page offers, a preview, and the apply. Multipart, so the file
        // is not held to the 100 kB JSON limit.
        getOnboardingFormat: builder.query<OnboardingImportFormat, void>({
            query: () => ({ url: '/onboarding-import/format', method: 'GET' }),
        }),
        previewOnboardingImport: builder.mutation<OnboardingImportReport, OnboardingImportArgs>({
            query: (args) => ({
                url: onboardingImportUrl(args.slug, true),
                method: 'POST',
                data: onboardingImportForm(args),
            }),
        }),
        applyOnboardingImport: builder.mutation<OnboardingImportReport, OnboardingImportArgs>({
            query: (args) => ({
                url: onboardingImportUrl(args.slug, false),
                method: 'POST',
                data: onboardingImportForm(args),
            }),
            // The account sections change; the other slices it fills (rooms,
            // organisations, classes, prices) are evicted by the page.
            invalidatesTags: ['Account', 'TenantAccount'],
        }),
        updateInvoiceIssuer: builder.mutation<AccountResponse, UpdateInvoiceIssuerRequest>({
            query: ({ invoiceIssuer, slug }) => ({
                url: slug
                    ? `/tenants/${encodeURIComponent(slug)}/invoice-issuer`
                    : '/account/invoice-issuer',
                method: 'PATCH',
                data: { invoiceIssuer },
            }),
            // The by-slug write changes the viewed tenant's account too.
            invalidatesTags: ['Account', 'TenantAccount'],
        }),
        /**
         * Where issued invoices are mailed when the booking's customer is not
         * the recipient (invoice dispatch, precedence level 2). `slug`
         * addresses another tenant as a master.
         */
        updateInvoiceDispatchEmail: builder.mutation<
            AccountResponse,
            { invoiceDispatchEmail: string | null, slug?: string }
        >({
            query: ({ invoiceDispatchEmail, slug }) => ({
                url: slug
                    ? `/tenants/${encodeURIComponent(slug)}/invoice-dispatch-email`
                    : '/account/invoice-dispatch-email',
                method: 'PATCH',
                data: { invoiceDispatchEmail },
            }),
            // Both, for the same reason the issuer write does: the by-slug
            // variant changes the VIEWED tenant's account, not the caller's.
            invalidatesTags: ['Account', 'TenantAccount'],
        }),
        /**
         * Fold same-rate extras into the stay line on issued invoices (#3427).
         * `slug` addresses another tenant as a master.
         */
        updateInvoiceBundleExtras: builder.mutation<
            AccountResponse,
            { invoiceBundleExtrasIntoStay: boolean, slug?: string }
        >({
            query: ({ invoiceBundleExtrasIntoStay, slug }) => ({
                url: slug
                    ? `/tenants/${encodeURIComponent(slug)}/invoice-bundle-extras`
                    : '/account/invoice-bundle-extras',
                method: 'PATCH',
                data: { invoiceBundleExtrasIntoStay },
            }),
            invalidatesTags: ['Account', 'TenantAccount'],
        }),
        // Which provider the tenant WANTS — a wish, never a status (PO,
        // 2026-08-19). Same two-route pattern as the invoice issuer.
        updateDesiredPaymentProvider: builder.mutation<
            AccountResponse,
            UpdateDesiredPaymentProviderRequest
        >({
            query: ({ desiredPaymentProvider, slug }) => ({
                url: slug
                    ? `/tenants/${encodeURIComponent(slug)}/payment-provider`
                    : '/account/payment-provider',
                method: 'PATCH',
                data: { desiredPaymentProvider },
            }),
            invalidatesTags: ['Account', 'TenantAccount'],
        }),
        updatePaymentsConfig: builder.mutation<AccountResponse, UpdatePaymentsConfigRequest>({
            query: (body) => ({
                url: '/account/payments',
                method: 'PATCH',
                data: body,
            }),
            invalidatesTags: ['Account'],
        }),
        // End-user "ideas" home card — toggle + texts (tenant.config.moreToCome).
        // Tenant context travels in X-Tenant like the payments write above, so
        // a master editing a viewed tenant hits the same route. Invalidates the
        // viewed-tenant read too, so the master sees the saved state.
        updateMoreToCome: builder.mutation<AccountResponse, UpdateMoreToComeRequest>({
            query: (body) => ({
                url: '/account/more-to-come',
                method: 'PATCH',
                data: body,
            }),
            invalidatesTags: ['Account', 'TenantAccount'],
        }),
        // The series season (series booking, step 8). With a slug, the master
        // by-slug route writes the VIEWED tenant (the own-tenant route would 404
        // for a master, or write the wrong tenant).
        updateSeriesSeason: builder.mutation<AccountResponse, UpdateSeriesSeasonRequest>({
            query: ({ slug, ...body }) => ({
                url: slug
                    ? `/tenants/${encodeURIComponent(slug)}/series-season`
                    : '/account/series-season',
                method: 'PATCH',
                data: body,
            }),
            invalidatesTags: ['Account', 'TenantAccount'],
        }),
        // The holiday region (M9, #3041). Same own/master-by-slug pair as the
        // series season.
        updateHolidayRegion: builder.mutation<AccountResponse, UpdateHolidayRegionRequest>({
            query: ({ slug, ...body }) => ({
                url: slug
                    ? `/tenants/${encodeURIComponent(slug)}/holiday-region`
                    : '/account/holiday-region',
                method: 'PATCH',
                data: body,
            }),
            invalidatesTags: ['Account', 'TenantAccount', 'HolidayStatus'],
        }),
        // `GET /admin/holidays/status` — the X-Tenant tenant's region and the
        // data's version, load date and reach.
        getHolidayStatus: builder.query<HolidayStatus, void>({
            query: () => ({ url: '/holidays/status', method: 'GET' }),
            providesTags: ['HolidayStatus'],
        }),
        // `GET /admin/holidays?from&to` — the X-Tenant tenant's holidays per
        // date (dashboard availability chart). A region change refetches it.
        getHolidaysInRange: builder.query<HolidaysInRange, { from: string, to: string }>({
            query: ({ from, to }) => ({ url: '/holidays', method: 'GET', params: { from, to } }),
            providesTags: ['HolidayStatus'],
        }),
        updatePriceEntry: builder.mutation<AccountResponse, UpdatePriceEntryRequest>({
            query: ({ slug, ...body }) => ({
                url: slug
                    ? `/tenants/${encodeURIComponent(slug)}/price-entry`
                    : '/account/price-entry',
                method: 'PATCH',
                data: body,
            }),
            invalidatesTags: ['Account', 'TenantAccount'],
        }),
        updateCheckInCheckOutConfig: builder.mutation<AccountResponse, UpdateCheckInCheckOutConfigRequest>({
            query: (body) => ({
                url: '/account/check-in-checkout',
                method: 'PATCH',
                data: body,
            }),
            invalidatesTags: ['Account'],
        }),
        // PATCH /admin/tenants/:slug/check-in-checkout — master-only by-slug
        // write of a VIEWED tenant's checkInCheckOutFlow (the master isn't a
        // member of it, so the owner's caller's-own-tenant endpoint above can't
        // target it). Backend gates on the `master` role. Returns { tenant }.
        updateTenantCheckInCheckOutConfig: builder.mutation<
            TenantAccountResponse,
            { slug: string, body: UpdateCheckInCheckOutConfigRequest }
        >({
            query: ({ slug, body }) => ({
                url: `/tenants/${slug}/check-in-checkout`,
                method: 'PATCH',
                data: body,
            }),
            invalidatesTags: (_result, _error, { slug }) => [{ type: 'TenantAccount', id: slug }],
        }),
        // POST /admin/account/resend-verification — asks Auth0 to send a
        // fresh email-verification link. Identifies the user via JWT sub;
        // no body. Backend always responds 200 with a constant message
        // (#954) so we never branch on success vs. rate-limit vs. already-
        // verified — the UI shows the same confirmation either way.
        // Intentionally no `invalidatesTags`: the email_verified flag
        // lives on the Auth0 JWT, not in the Account cache, so the recheck
        // path (loginWithRedirect) is what refreshes it.
        resendVerificationEmail: builder.mutation<ResendVerificationResponse, void>({
            query: () => ({
                url: '/account/resend-verification',
                method: 'POST',
            }),
        }),
        // POST /admin/account/deletion-request — "Löschung beantragen" (#1379
        // interim). Resolved: {newBackendUrl}=…/admin + /account/deletion-request.
        // Mails the request to kolula; writes no tenant data, so it invalidates
        // nothing. Success only when the server confirms both mails went out.
        requestAccountDeletion: builder.mutation<AccountDeletionRequestResponse, AccountDeletionRequestBody>({
            query: (body) => ({
                url: '/account/deletion-request',
                method: 'POST',
                data: body,
            }),
        }),
        // GET /admin/account/cancellation — the upcoming bookings that block
        // "Cancel account" (#1379). Resolved: {newBackendUrl}=…/admin +
        // /account/cancellation. Read only while the dialog is open.
        getAccountCancellation: builder.query<AccountCancellationResponse, void>({
            query: () => ({
                url: '/account/cancellation',
                method: 'GET',
            }),
            providesTags: ['AccountCancellation'],
        }),
        // POST /admin/account/cancel — archives the caller's tenant at once
        // (#1379). Resolved: …/admin + /account/cancel. The UI logs the owner
        // out on success, so nothing needs refetching; a 422 carries the
        // upcoming count, so the dialog's read is refreshed.
        cancelAccount: builder.mutation<AccountCancelResponse, AccountDeletionRequestBody>({
            query: (body) => ({
                url: '/account/cancel',
                method: 'POST',
                data: body,
            }),
            invalidatesTags: ['AccountCancellation'],
        }),
        // POST /admin/tenants/:slug/reactivate — master-only (#1379). Resolved:
        // …/admin + /tenants/:slug/reactivate. Refreshes the directory (the
        // archived mark) and the viewed tenant's account.
        reactivateTenant: builder.mutation<TenantAccountResponse, string>({
            query: (slug) => ({
                url: `/tenants/${slug}/reactivate`,
                method: 'POST',
            }),
            invalidatesTags: (_result, _error, slug) => ['TenantDirectory', { type: 'TenantAccount', id: slug }],
        }),
        // GET /admin/master/tenants — master-only (403 otherwise). The page
        // "Mandanten": every operational tenant with owner, plan, contract, counts.
        getMasterTenants: builder.query<MasterTenantRow[], void>({
            query: () => ({
                url: '/master/tenants',
                method: 'GET',
            }),
            providesTags: ['MasterTenants'],
        }),
        // POST /admin/master/tenants/:slug/contract — master-only. Enters kolula's
        // contract with the tenant (ends the current one, lifts the trial).
        enterTenantContract: builder.mutation<MasterTenantRow, EnterTenantContractRequest>({
            query: ({ slug, ...body }) => ({
                url: `/master/tenants/${encodeURIComponent(slug)}/contract`,
                method: 'POST',
                data: body,
            }),
            invalidatesTags: (_result, _error, { slug }) => ['MasterTenants', { type: 'TenantAccount', id: slug }],
        }),
    }),
});

export const {
    useGetOnboardingFormatQuery,
    usePreviewOnboardingImportMutation,
    useApplyOnboardingImportMutation,
    useRegisterTenantMutation,
    useGetAccountQuery,
    useLazyGetAccountQuery,
    useLazyGetAccountPermissionsQuery,
    useGetAccountUsersQuery,
    useCreateTenantUserMutation,
    useUpdateTenantUserMutation,
    useDeleteTenantUserMutation,
    useGetAllTenantsQuery,
    useLazyGetAllTenantsQuery,
    useGetTenantInvoiceInfoQuery,
    useGetTenantAccountQuery,
    useLazyGetTenantAccountQuery,
    useUpdateProfileMutation,
    useUpdateOrganizationMutation,
    useUpdateBankDetailsMutation,
    useUpdateContactMutation,
    useUpdateDesiredPaymentProviderMutation,
    useUpdateInvoiceDispatchEmailMutation,
    useUpdateInvoiceBundleExtrasMutation,
    useUpdateInvoiceIssuerMutation,
    useUpdateLegalTextsMutation,
    useAddOwnTermsDocumentMutation,
    useUpdateOwnTermsDocumentMutation,
    useRemoveOwnTermsDocumentMutation,
    useReorderOwnTermsDocumentsMutation,
    useAddFaqMutation,
    useUpdateFaqMutation,
    useRemoveFaqMutation,
    useReorderFaqsMutation,
    useUpdatePrivacyOfficerMutation,
    useUpdateSeoMutation,
    useUpdateLegalNoticeMutation,
    useUpdateBookingPageActivationMutation,
    useUpdateAnredeMutation,
    useGetSenderIdentityQuery,
    useSetSenderIdentityMutation,
    useRemoveSenderIdentityMutation,
    useUpdateBrandingMutation,
    useUpdateCheckInCheckOutConfigMutation,
    useGetPaymentIntegrationQuery,
    useUpdatePaymentsConfigMutation,
    useUpdateMoreToComeMutation,
    useUpdateSeriesSeasonMutation,
    useUpdateHolidayRegionMutation,
    useGetHolidayStatusQuery,
    useGetHolidaysInRangeQuery,
    useUpdatePriceEntryMutation,
    useUpdateServicesMutation,
    useUpdateTenantCheckInCheckOutConfigMutation,
    useResendVerificationEmailMutation,
    useRequestAccountDeletionMutation,
    useGetAccountCancellationQuery,
    useCancelAccountMutation,
    useReactivateTenantMutation,
    useGetMasterTenantsQuery,
    useEnterTenantContractMutation,
} = accountApiSlice;
